import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiFetch } from "../utils/apiFetch";

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

const METODOS = ["Efectivo","Bizum","Tarjeta","Transferencia","Factura"];

const ESTADOS_SUGERIDOS = ["Entregado","FACTURA","AGUARDANDO","PENDIENTE"];

const fmt = (num) =>
  num != null && num > 0
    ? `${parseFloat(num).toFixed(2).replace(".", ",")} €`
    : "";

// ── Celda editable ─────────────────────────────────────────────────────────────
function EditableCell({ value, onSave, type = "text", options = null, sugerencias = null, style = {} }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  const inputRef = useRef();

  useEffect(() => { setVal(value ?? ""); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    const trimmed = type === "number" ? val : (typeof val === "string" ? val.trim() : val);
    if (String(trimmed) !== String(value ?? "")) onSave(trimmed);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") commit();
    if (e.key === "Escape") { setEditing(false); setVal(value ?? ""); }
  };

  if (!editing) {
    return (
      <span
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
        role="button"
        tabIndex={0}
        title="Clic para editar"
        style={{ cursor: "text", display: "block", minHeight: "20px", outline: "none", ...style }}
      >
        {value ?? ""}
      </span>
    );
  }

  if (options) {
    return (
      <select
        ref={inputRef}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        style={{ width: "100%", border: "2px solid #1a73e8", background: "#fff", color: "#202124", padding: "2px 3px", fontSize: "13px", fontFamily: "Arial, sans-serif" }}
      >
        <option value=""></option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        ref={inputRef}
        type={type}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={onKeyDown}
        list={sugerencias ? `sug-${Math.random()}` : undefined}
        style={{ width: "100%", border: "2px solid #1a73e8", background: "#fff", color: "#202124", padding: "2px 3px", fontSize: "13px", fontFamily: "Arial, sans-serif" }}
      />
      {sugerencias && (
        <datalist id={`sug-${val}`}>
          {sugerencias.map((s) => <option key={s} value={s} />)}
        </datalist>
      )}
    </div>
  );
}

// ── Página principal ───────────────────────────────────────────────────────────
export default function TablaRegistrosPage() {
  const today = new Date();
  const [mes, setMes] = useState(today.getMonth() + 1);
  const [anio, setAnio] = useState(today.getFullYear());
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(null); // id de la fila que se está guardando
  const [error, setError] = useState(null);

  const fetchRegistros = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch(`/api/tabla-registros?mes=${mes}&anio=${anio}`);
      setRegistros(data || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mes, anio]);

  useEffect(() => { fetchRegistros(); }, [fetchRegistros]);

  const saveCell = async (id, campo, valor) => {
    setSaving(id);
    try {
      const updated = await apiFetch(`/api/tabla-registros/${id}`, {
        method: "PATCH",
        body: { [campo]: valor },
      });
      setRegistros((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ...updated } : r))
      );
    } catch (e) {
      alert(`Error al guardar: ${e.message}`);
    } finally {
      setSaving(null);
    }
  };

  const getRowStatus = (row) => {
    if (row.entregado) {
      return row.es_concesionario ? "entregado-concesionario" : "entregado-particular";
    }
    if (!row.es_concesionario) return "pendiente-particular";
    return "";
  };

  const totalPrecio = registros.reduce((s, r) => s + (parseFloat(r.precio) || 0), 0);
  const totalIva = registros.reduce((s, r) => s + (parseFloat(r.iva) || 0), 0);

  // Generar opciones de mes (últimos 12 meses)
  const mesesOpciones = [];
  for (let i = 0; i < 12; i++) {
    let m = today.getMonth() + 1 - i;
    let a = today.getFullYear();
    if (m <= 0) { m += 12; a -= 1; }
    mesesOpciones.push({ mes: m, anio: a, label: `${MESES[m - 1]} ${a}` });
  }

  const cellStyle = {
    border: "1px solid #d9d9d9",
    padding: "4px 6px",
    height: "21px",
    fontSize: "13px",
    fontFamily: "Arial, Calibri, sans-serif",
    textAlign: "center",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    background: "#fff",
    color: "#202124",
  };
  const thStyle = {
    ...cellStyle,
    background: "#fff",
    fontWeight: "600",
    color: "#333",
    textAlign: "center",
    whiteSpace: "normal",
    position: "sticky",
    top: 0,
    zIndex: 2,
    borderBottom: "1px solid #8a8a8a",
  };

  return (
    <>
    <style>{`
      .sw-tabla-reg-wrap,
      .sw-tabla-reg-wrap * { color-scheme: only light; }
      .sw-tabla-reg-wrap table { background: #fff; }
      .sw-tabla-reg-wrap td,
      .sw-tabla-reg-wrap th { background-color: #fff !important; color: #202124 !important; border-color: #d9d9d9 !important; }
      .sw-tabla-reg-wrap tr[data-status="pendiente-particular"] td { color: #c2185b !important; }
      .sw-tabla-reg-wrap tr[data-status="entregado-particular"] td { color: #187018 !important; }
      .sw-tabla-reg-wrap tr[data-status="entregado-concesionario"] td { color: #1a65b5 !important; }
      .sw-tabla-reg-wrap input,
      .sw-tabla-reg-wrap select { background-color: #fff !important; color: inherit !important; }
      .sw-tabla-reg-wrap button { background: #f9f9f9 !important; color: #202124 !important; border-color: #aaa !important; }
    `}</style>
    <div className="sw-tabla-reg-wrap" style={{ padding: "16px", fontFamily: "Arial, sans-serif", background: "#f6f7f9", color: "#202124", minHeight: "100%" }}>
      {/* Cabecera */}
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", flexWrap: "wrap" }}>
        <h4 style={{ margin: 0, fontWeight: "bold", color: "#202124" }}>Tabla de Registros</h4>
        <button
          onClick={fetchRegistros}
          style={{ padding: "4px 12px", fontSize: "12px", cursor: "pointer", borderRadius: "4px", border: "1px solid #aaa", background: "#f9f9f9", color: "#202124" }}
        >
          Actualizar
        </button>
      </div>

      {/* Selector de mes */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "12px", flexWrap: "wrap" }}>
        {mesesOpciones.map(({ mes: m, anio: a, label }) => {
          const activo = m === mes && a === anio;
          return (
            <button
              key={label}
              onClick={() => { setMes(m); setAnio(a); }}
              style={{
                padding: "4px 12px",
                fontSize: "12px",
                cursor: "pointer",
                borderRadius: "4px",
                border: activo ? "2px solid #f5a623" : "1px solid #ccc",
                background: activo ? "#ffd700" : "#f9f9f9", color: "#202124",
                fontWeight: activo ? "bold" : "normal",
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {error && (
        <div style={{ background: "#ffeeba", border: "1px solid #ffc107", padding: "8px", marginBottom: "12px", borderRadius: "4px" }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px", color: "#595959" }}>Cargando...</div>
      ) : (
        <>
          {/* Título del mes (como en Sheets) */}
          <div style={{ background: "#fff", fontWeight: "600", textAlign: "center", padding: "6px", border: "1px solid #d9d9d9", borderBottom: 0, fontSize: "13px", color: "#444" }}>
            {MESES[mes - 1]} {anio}
          </div>

          <div style={{ overflowX: "auto", background: "#fff", border: "1px solid #c9c9c9", boxShadow: "0 2px 8px rgba(0,0,0,.06)" }}>
            <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: "1353px" }}>
              <colgroup>
                <col style={{ width: "100px" }} />
                <col style={{ width: "186px" }} />
                <col style={{ width: "111px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "254px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "100px" }} />
                <col style={{ width: "202px" }} />
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle}>Fecha</th>
                  <th style={thStyle}>Modelo/Marca</th>
                  <th style={thStyle}>Empresa/Particular</th>
                  <th style={thStyle}>Matrícula</th>
                  <th style={{ ...thStyle, maxWidth: "220px" }}>Tipo de Limpieza</th>
                  <th style={thStyle}>Precio facturado</th>
                  <th style={thStyle}>IVA</th>
                  <th style={thStyle}>Forma / Factura</th>
                  <th style={thStyle}>Entrega</th>
                  <th style={{ ...thStyle, maxWidth: "180px" }}>Observaciones</th>
                  <th style={thStyle}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {registros.length === 0 && (
                  <tr>
                    <td colSpan={11} style={{ ...cellStyle, textAlign: "center", color: "#595959", padding: "20px" }}>
                      No hay registros para {MESES[mes - 1]} {anio}
                    </td>
                  </tr>
                )}
                {registros.map((row) => {
                  const rowStatus = getRowStatus(row);
                  const isSaving = saving === row.id;
                  return (
                    <tr
                      key={row.id}
                      data-status={rowStatus}
                      style={{ background: isSaving ? "#fffde7" : "white", transition: "background 0.3s" }}
                    >
                      <td style={cellStyle}>{row.fecha}</td>
                      <td style={cellStyle}>{row.modelo}</td>
                      <td style={cellStyle}>{row.cliente}</td>
                      <td style={{ ...cellStyle, fontWeight: "500" }}>{row.matricula}</td>
                      <td title={row.servicios} style={{ ...cellStyle, whiteSpace: "normal", height: "auto", minHeight: "21px" }}>{row.servicios}</td>

                      {/* Importe facturado: Sheets es la fuente, no implica que esté cobrado. */}
                      <td
                        style={cellStyle}
                        title={row.fuente_facturacion === "google_sheets" ? "Importado de Google Sheets" : "Calculado por el sistema"}
                      >
                        {fmt(row.precio)}
                      </td>

                      {/* IVA — solo lectura, calculado */}
                      <td style={cellStyle}>
                        {fmt(row.iva)}
                      </td>

                      {/* Método de Pago — editable (dropdown) */}
                      <td style={{ ...cellStyle, minWidth: "110px" }}>
                        <EditableCell
                          value={row.metodo}
                          options={METODOS}
                          onSave={(v) => saveCell(row.id, "metodo", v)}
                        />
                      </td>

                      {/* Fecha entrega — editable */}
                      <td style={{ ...cellStyle, minWidth: "90px" }}>
                        <EditableCell
                          value={row.fecha_entrega}
                          type="text"
                          onSave={(v) => saveCell(row.id, "fecha_entrega", v)}
                        />
                      </td>

                      {/* Observaciones — editable */}
                      <td style={{ ...cellStyle, whiteSpace: "normal", height: "auto", minHeight: "21px" }}>
                        <EditableCell
                          value={row.observaciones}
                          type="text"
                          onSave={(v) => saveCell(row.id, "observaciones", v)}
                        />
                      </td>

                      {/* Estado — editable con sugerencias */}
                      <td style={{ ...cellStyle, minWidth: "100px" }}>
                        <EditableCell
                          value={row.estado}
                          sugerencias={ESTADOS_SUGERIDOS}
                          onSave={(v) => saveCell(row.id, "estado", v)}
                        />
                      </td>
                    </tr>
                  );
                })}

                {/* Igual que en Excel: una fila vacia antes del Total */}
                {registros.length > 0 && (
                  <>
                    <tr aria-hidden="true">
                      {Array.from({ length: 11 }).map((_, index) => (
                        <td key={index} style={{ ...cellStyle, height: "21px" }}></td>
                      ))}
                    </tr>
                    <tr style={{ fontWeight: "600" }}>
                      <td style={cellStyle}>Total</td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}>{fmt(totalPrecio) || "0,00 €"}</td>
                      <td style={cellStyle}>{fmt(totalIva) || "0,00 €"}</td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                      <td style={cellStyle}></td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Leyenda */}
          <div style={{ marginTop: "12px", fontSize: "13px", color: "#666", display: "flex", gap: "16px", flexWrap: "wrap" }}>
            <span><span style={{ color: "#c2185b" }}>■</span> Particular (pendiente)</span>
            <span><span style={{ color: "#187018" }}>■</span> Particular entregado</span>
            <span><span style={{ color: "#1a65b5" }}>■</span> Concesionario entregado</span>
            <span style={{ color: "#595959" }}>Clic en una celda para editar · Los cambios se sincronizan con Google Sheets</span>
          </div>
        </>
      )}
    </div>
    </>
  );
}
